import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import UnifiedFileUpload from '@/components/ui/UnifiedFileUpload';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Upload, 
  Star, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Image as ImageIcon,
  Trash2,
  Eye,
  Calendar,
  Hash,
  MessageSquare
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface ThirdPartySubmission {
  id: string;
  platform_name: string;
  order_count: number | null;
  review_count: number | null;
  rating: number | null;
  status: string;
  admin_notes: string | null;
  screenshot_urls: string[];
  created_at: string;
  updated_at: string;
}

interface ThirdPartyRatingDashboardProps {
  userId: string;
}

export const ThirdPartyRatingDashboard: React.FC<ThirdPartyRatingDashboardProps> = ({ userId }) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submissions, setSubmissions] = useState<ThirdPartySubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    platform_name: '',
    order_count: '',
    review_count: '',
    rating: '',
  });

  useEffect(() => {
    if (userId) {
      fetchSubmissions();
    }
  }, [userId]);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('third_party_rating_submissions')
        .select('*')
        .eq('practitioner_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load submissions'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      
      // Validate file types and sizes
      const validFiles = files.filter(file => {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        
        if (!validTypes.includes(file.type)) {
          toast({
            variant: 'destructive',
            title: 'Invalid file type',
            description: 'Please upload only JPEG or PNG images'
          });
          return false;
        }
        
        if (file.size > maxSize) {
          toast({
            variant: 'destructive',
            title: 'File too large',
            description: 'Please upload images smaller than 5MB'
          });
          return false;
        }
        
        return true;
      });

      // Limit to 5 files maximum
      const limitedFiles = validFiles.slice(0, 5);
      if (validFiles.length > 5) {
        toast({
          variant: 'destructive',
          title: 'Too many files',
          description: 'Maximum 5 screenshots allowed per submission'
        });
      }

      setSelectedFiles(limitedFiles);

      // Generate preview URLs
      const previews = limitedFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(previews);
    }
  };

  const uploadScreenshots = async (submissionId: string): Promise<string[]> => {
    if (selectedFiles.length === 0) return [];

    const uploadedUrls: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${userId}/third-party-screenshots/${submissionId}/${fileName}`;

      try {
        const { data, error } = await supabase.storage
          .from('practitioner_files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (error) throw error;
        uploadedUrls.push(filePath);
      } catch (error) {
        console.error('Error uploading screenshot:', error);
        throw error;
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.platform_name.trim()) {
      toast({
        variant: 'destructive',
        title: 'Platform name required',
        description: 'Please enter the platform name'
      });
      return;
    }

    if (selectedFiles.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Screenshots required',
        description: 'Please upload at least one screenshot as proof'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create submission first to get ID
      const submissionData = {
        practitioner_id: userId,
        platform_name: formData.platform_name.trim(),
        order_count: formData.order_count ? parseInt(formData.order_count) : null,
        review_count: formData.review_count ? parseInt(formData.review_count) : null,
        rating: formData.rating ? parseFloat(formData.rating) : null,
        screenshot_urls: [], // Will be updated after upload
      };

      const { data: submission, error } = await supabase
        .from('third_party_rating_submissions')
        .insert(submissionData)
        .select()
        .single();

      if (error) throw error;

      // Upload screenshots and update submission
      const screenshotUrls = await uploadScreenshots(submission.id);
      
      const { error: updateError } = await supabase
        .from('third_party_rating_submissions')
        .update({ screenshot_urls: screenshotUrls })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      toast({
        title: 'Submission successful',
        description: 'Your third-party rating data has been submitted for review'
      });

      // Reset form
      setFormData({
        platform_name: '',
        order_count: '',
        review_count: '',
        rating: '',
      });
      setSelectedFiles([]);
      setPreviewUrls([]);

      // Refresh submissions
      fetchSubmissions();
    } catch (error) {
      console.error('Error submitting data:', error);
      toast({
        variant: 'destructive',
        title: 'Submission failed',
        description: 'Failed to submit your data. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const viewScreenshot = async (filePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('practitioner_files')
        .createSignedUrl(filePath, 60);

      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing screenshot:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load screenshot'
      });
    }
  };

  return (
    <Tabs defaultValue="submit" className="space-y-6">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="submit">Submit New Data</TabsTrigger>
        <TabsTrigger value="history">Submission History</TabsTrigger>
      </TabsList>

      <TabsContent value="submit">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Submit Third-Party Platform Data
            </CardTitle>
            <CardDescription>
              Upload your ratings and order data from other platforms with verification screenshots. 
              Once approved, this data will be displayed on your profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="platform_name">Platform Name *</Label>
                  <Input
                    id="platform_name"
                    placeholder="e.g., BetterHelp, Psychology Today"
                    value={formData.platform_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, platform_name: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rating">Average Rating (1-5)</Label>
                  <Input
                    id="rating"
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    placeholder="e.g., 4.8"
                    value={formData.rating}
                    onChange={(e) => setFormData(prev => ({ ...prev, rating: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="order_count">Total Orders/Sessions</Label>
                  <Input
                    id="order_count"
                    type="number"
                    min="0"
                    placeholder="e.g., 150"
                    value={formData.order_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, order_count: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="review_count">Number of Reviews</Label>
                  <Input
                    id="review_count"
                    type="number"
                    min="0"
                    placeholder="e.g., 89"
                    value={formData.review_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, review_count: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Screenshot Upload */}
              <div className="space-y-4">
                <div>
                  <Label>Verification Screenshots *</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload screenshots showing your ratings and statistics on the platform
                  </p>
                </div>
                
                <div className="space-y-4">
                  <UnifiedFileUpload
                    accept="image/jpeg,image/png,image/jpg"
                    multiple={true}
                    maxFiles={5}
                    maxSize={5}
                    onFilesChange={(files) => {
                      setSelectedFiles(files);
                      // Create preview URLs
                      const urls = files.map(file => URL.createObjectURL(file));
                      setPreviewUrls(urls);
                    }}
                    disabled={isSubmitting}
                    formatHint="Upload screenshots (JPEG, PNG up to 5MB each)"
                  >
                    Upload Screenshots
                  </UnifiedFileUpload>
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Upload className="h-4 w-4 mr-2 animate-pulse" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Submit for Review
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="history">
        <Card>
          <CardHeader>
            <CardTitle>Submission History</CardTitle>
            <CardDescription>
              Track the status of your third-party platform data submissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No submissions yet</p>
                <p className="text-sm">Submit your first third-party platform data to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <Card key={submission.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            {submission.platform_name}
                            {getStatusIcon(submission.status)}
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(submission.created_at).toLocaleDateString()}
                            </span>
                            {submission.screenshot_urls.length > 0 && (
                              <span className="flex items-center gap-1">
                                <ImageIcon className="h-3 w-3" />
                                {submission.screenshot_urls.length} screenshot{submission.screenshot_urls.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge className={getStatusColor(submission.status)}>
                          {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Star className="h-3 w-3" />
                            Rating:
                          </span>
                          <p className="font-medium">
                            {submission.rating ? submission.rating.toFixed(1) : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            Orders:
                          </span>
                          <p className="font-medium">
                            {submission.order_count ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            Reviews:
                          </span>
                          <p className="font-medium">
                            {submission.review_count ?? 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Screenshots */}
                      {submission.screenshot_urls.length > 0 && (
                        <div className="mb-4">
                          <Label className="text-sm font-medium">Screenshots:</Label>
                          <div className="flex gap-2 mt-2">
                            {submission.screenshot_urls.map((url, index) => (
                              <Button
                                key={index}
                                variant="outline"
                                size="sm"
                                onClick={() => viewScreenshot(url)}
                                className="flex items-center gap-1"
                              >
                                <Eye className="h-3 w-3" />
                                View {index + 1}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {submission.admin_notes && (
                        <div className="p-3 bg-muted rounded-lg">
                          <p className="text-sm">
                            <strong>Admin Notes:</strong> {submission.admin_notes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};